/* eslint-disable react-hooks/exhaustive-deps */
import {
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FaBookmark, FaShapes } from 'react-icons/fa';
import { GiAmericanFootballHelmet } from 'react-icons/gi';
import { LuImage, LuType } from 'react-icons/lu';
import { PiBoundingBox } from 'react-icons/pi';
import { useAPIClient } from '../../../context/apiClient';
import {
  useComponentImportDispatch,
  useComponentImportState,
} from '../../../context/componentImport';
import {
  AdElement,
  AdElementImage,
  AdElementTeamAsset,
  AdElementCasinoAsset,
  Datasources,
  SoccerStages,
  isImageElement,
  isSVGElement,
  isTeamAssetElement,
  isTextElement,
  isVectorElement,
  isCasinoAssetElement,
  parseElementName,
  buildCustomAssetContentUrl,
} from '@adflow/types';
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Icon,
  Image,
  ModalBody,
  ModalFooter,
  Skeleton,
  Stack,
  Text,
  useBoolean,
} from '@chakra-ui/react';
import { EditorStoreContext } from '@adflow/editor';
import React from 'react';
import { StepProps } from '..';
import ImgElTypeRadioGroup, { ImgElType } from './ImgElTypeRadioGroup';
import DataMappingMagic from './DataMappingMagic';
import { elementSelectionHelper } from './elementSelection.helpers';
import { UserContext } from '@adflow/utils';
import { CasinoIcon } from '@adflow/editor/src/icons/casino';

export type StepThreeSubmit = (
  customElements: Array<AdElement>,
  thumbnailURL: string,
  dataSourceTypes?: Array<Datasources.SourceType>,
) => void;

interface StepThreeProps extends StepProps {
  onSubmit: StepThreeSubmit;
}

const ComponentImportStepThree: FC<PropsWithChildren<StepThreeProps>> = ({ onClose, onSubmit }) => {
  const ctx = useContext(EditorStoreContext);
  const state = useComponentImportState();
  const [teamAssetElIds, setTeamAssetElIds] = useState<Array<string>>([]);
  const [casinoAssetElIds, setCasinoAssetElIds] = useState<Array<string>>([]);
  const [isSaved, setIsSaved] = useState(false);
  const dispatch = useComponentImportDispatch();
  const client = useAPIClient();

  const [casinoTemplatesEnabled, setCasinoTemplatesEnabled] = useState(false);
  const [soccerMomentsEnabled, setSoccerMomentsEnabled] = useState(false);
  const [stageOverride, setStageOverride] = useState<SoccerStages | null>(null);
  const { user } = useContext(UserContext);

  const isRunningInsideCypress =
    typeof window !== 'undefined' && Boolean((window as unknown as { Cypress?: boolean }).Cypress);

  useEffect(() => {
    const fetchCasinoTemplateFeature = async () => {
      const hasCasinoTemplate = (await user?.hasFeature('cm-casino-templates')) ?? false;
      setCasinoTemplatesEnabled(hasCasinoTemplate || isRunningInsideCypress);
    };
    fetchCasinoTemplateFeature();
  }, [user]);

  useEffect(() => {
    const fetchSoccerMomentsFeature = async () => {
      const cypressOverride = import.meta.env.VITE_SOCCER_MOMENTS_AVAILABLE_CYPRESS === 'true';
      const hasSoccerMoments =
        cypressOverride || ((await user?.hasFeature('cm-soccer-moments')) ?? false);
      setSoccerMomentsEnabled(hasSoccerMoments);
    };
    fetchSoccerMomentsFeature();
  }, [user]);

  const toggleSaveComponent = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSaved(e.target.checked);
  }, []);

  const suggestedDataSources = useMemo(
    () =>
      Datasources.suggestDataSources([
        ...state.elements.filter(isTextElement),
        ...state.elements.filter(isImageElement),
      ]),
    [state.elements],
  );

  const inferredSoccerConfig = useMemo(
    () =>
      soccerMomentsEnabled ? Datasources.inferSoccerMomentsConfig(suggestedDataSources) : null,
    [suggestedDataSources, soccerMomentsEnabled],
  );

  const confirmedStage = stageOverride ?? inferredSoccerConfig?.stage;

  const filteredSuggestions = useMemo(() => {
    if (!soccerMomentsEnabled) {
      return Datasources.filterSoccerSuggestions(suggestedDataSources);
    }
    if (inferredSoccerConfig) {
      let filtered = Datasources.filterNonSoccerSportsSuggestions(suggestedDataSources);
      filtered = Datasources.filterNonSoccerTemplates(filtered);
      if (confirmedStage != null) {
        filtered = Datasources.filterToSoccerStage(filtered, confirmedStage);
      } else {
        filtered = Datasources.filterToSoccerCommonOnly(filtered);
      }
      return filtered;
    }
    return Datasources.filterSoccerSuggestions(suggestedDataSources);
  }, [suggestedDataSources, soccerMomentsEnabled, inferredSoccerConfig, confirmedStage]);

  const [mappingEnabled, { toggle: toggleDataMapping }] = useBoolean(
    filteredSuggestions.length > 0,
  );

  const dataSourceTypes = useMemo(
    () => filteredSuggestions.map((ds) => ds[0].type),
    [filteredSuggestions],
  );

  const [checkedElements, setCheckedElements] = useState(
    [...state.elements]
      .map((el) => {
        return { selected: el.type === 'SVG', ...el };
      })
      .sort((a, b) => {
        if (a.selected && !b.selected) return -1;
        if (!a.selected && b.selected) return 1;
        return a.type.localeCompare(b.type) || a.name.localeCompare(b.name);
      })
      .map((el) => ({
        ...el,
        selected:
          filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)) ||
          el.type === 'SVG',
      })),
  );

  useEffect(() => {
    setCheckedElements((prev) =>
      prev.map((el) => ({
        ...el,
        selected:
          el.selected || filteredSuggestions.some((dps) => dps.some((dp) => dp.elId === el.id)),
      })),
    );
  }, [filteredSuggestions]);

  const lookupDataSource = useCallback(
    (elId: string) => {
      const matchingGroups = filteredSuggestions.filter((dps) =>
        dps.some((dp) => dp.elId === elId),
      );

      // When soccer moments is detected and multiple groups match, prefer soccer-type groups
      // over generic ones (e.g. prefer SOCCER/TEAM_HOME_COMPETITOR_ID over NEW_EVENT/HOME_COMPETITOR_ID
      // for HOME_CUSTOM elements, since both match via autoImportName aliases).
      const chosenGroup =
        inferredSoccerConfig && matchingGroups.length > 1
          ? matchingGroups.find((g) => Datasources.filterSoccerSuggestions([g]).length === 0) ??
            matchingGroups[0]
          : matchingGroups[0];

      const dataPoints = chosenGroup?.filter((dp) => dp.elId === elId);

      if (!dataPoints) {
        return {};
      }
      const dataPoint = dataPoints[0];
      const dataSource = ctx.dataSources.find((ds) => ds.type === dataPoint.type);

      if (!dataSource) {
        return {};
      }
      return { dataSource, dataPoint };
    },
    [filteredSuggestions, ctx.dataSources, inferredSoccerConfig],
  );

  const mapCustomImageElement = useCallback(
    (el: AdElementImage): AdElementImage => {
      const parsed = parseElementName(el.name);
      if (!mappingEnabled || !parsed.config?.customAssetConfig) return el;

      const { dataSource, dataPoint } = lookupDataSource(el.id);
      if (!dataSource || !dataPoint) return el;

      const contentUrl = buildCustomAssetContentUrl(
        parsed.config.customAssetConfig,
        dataPoint.dataPointName,
        dataSource.id,
      );

      return {
        ...el,
        data: {
          ...el.data,
          content: contentUrl,
        },
        sources: [
          ...el.sources,
          {
            sourceId: dataSource.id,
            name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
          },
        ],
      };
    },
    [mappingEnabled, lookupDataSource],
  );

  const handleSubmit: React.MouseEventHandler<HTMLButtonElement> = useCallback(
    async (evt) => {
      evt.preventDefault();
      const svgElements = state.elements.filter(isSVGElement);
      const textElements = state.elements.filter(isTextElement);
      const imageElements = state.elements.filter(isImageElement);
      const teamAssetElements = state.elements.filter(isTeamAssetElement);
      const casinoAssetElements = state.elements.filter(isCasinoAssetElement);
      const vectorElements = state.elements.filter(isVectorElement);

      const filteredElements = checkedElements
        .filter((el) => el.selected)
        .map((el) => {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
          const { selected, ...rest } = el;

          if (isTextElement(rest)) {
            if (!mappingEnabled) return rest;
            const { dataSource, dataPoint } = lookupDataSource(rest.id);
            if (!dataSource || !dataPoint) return rest;
            return {
              ...rest,
              data: {
                ...rest.data,
                content: `{${dataSource.id}:${dataPoint.dataPointName}}`,
              },
              sources: [
                ...rest.sources,
                {
                  sourceId: dataSource.id,
                  name: dataPoint.dataPointName as Datasources.AllowableDataPoints,
                },
              ],
            };
          } else if (isImageElement(rest)) {
            const parsed = parseElementName(rest.name);
            const isTeamAsset = teamAssetElIds.includes(rest.id);
            const isCasinoAsset = casinoAssetElIds.includes(rest.id);
            if (isTeamAsset) {
              const overrides = mappingEnabled ? lookupDataSource(rest.id) : {};
              return {
                ...rest,
                type: 'TEAM_ASSET',
                data: {
                  ...rest.data,
                  ...('dataPoint' in overrides
                    ? {
                        content: '',
                        source: elementSelectionHelper.mapDataPointToTeamAssetSource(
                          overrides.dataPoint?.dataPointName ?? '',
                        ),
                      }
                    : {}),
                },
              } as AdElementTeamAsset;
            }
            if (isCasinoAsset) {
              const overrides = mappingEnabled ? lookupDataSource(rest.id) : {};
              return {
                ...rest,
                position: { ...rest.position, z: -1 },
                type: 'CASINO_ASSET',
                data: {
                  ...rest.data,
                  ...('dataPoint' in overrides
                    ? {
                        content: '',
                        source: elementSelectionHelper.mapDataPointToCasinoAssetSource(
                          overrides.dataPoint?.dataPointName ?? '',
                        ),
                      }
                    : {}),
                },
              } as AdElementCasinoAsset;
            }
            if (parsed.config?.customAssetConfig && mappingEnabled) {
              return mapCustomImageElement(rest);
            }
            return rest;
          } else if (isVectorElement(rest)) {
            return elementSelectionHelper.mapVectorElement(rest, svgElements[0].data.svg);
          } else {
            return rest;
          }
        });

      if (mappingEnabled) {
        const assetType = filteredElements
          .filter(isTeamAssetElement)
          .map((el) => {
            const { dataPoint } = lookupDataSource(el.id);
            return dataPoint
              ? elementSelectionHelper.mapDataPointToDisplayAssetType(dataPoint.dataPointName)
              : undefined;
          })
          .filter((x) => x)
          .pop();
        if (assetType) {
          ctx.store?.getState().setDisplayOptions('teamAssets', {
            type: assetType,
            fallback: 'NONE',
          });
        }
      }

      onSubmit(
        filteredElements,
        state.componentThumbnailURL,
        mappingEnabled ? dataSourceTypes : [],
      );
      if (!isSaved) {
        return;
      }

      if (!svgElements.length) {
        console.warn('expected at least one SVG element but received 0 ');
        return;
      }
      if (!textElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      if (!imageElements.length) {
        console.warn('expected at least one text element but received 0 ');
        return;
      }
      const svgElement = svgElements[0];
      const svgString = svgElement.data.svg;
      try {
        await client.upsertFigmaComponent({
          figmaId: svgElement.id,
          fileId: state.fileId,
          name: svgElement.name,
          svg: svgString,
          svgUrl: state.previewURL,
          thumbnail: state.componentThumbnailURL,
          svgElement,
          textElements,
          imageElements,
          teamAssetElements,
          casinoAssetElements,
          vectorElements,
        });
      } catch (error) {
        dispatch({
          type: 'error',
          error: 'An unexpected error occurred',
        });
      }
    },
    [
      state.elements,
      state.componentThumbnailURL,
      state.fileId,
      state.previewURL,
      checkedElements,
      onSubmit,
      dataSourceTypes,
      isSaved,
      teamAssetElIds,
      casinoAssetElIds,
      mappingEnabled,
      lookupDataSource,
      ctx.store,
      client,
      dispatch,
    ],
  );

  const onChangeHandle = useCallback(
    (inputIndex: number) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setCheckedElements(
        checkedElements.map((el, idx) => {
          if (inputIndex === idx) {
            return { ...el, selected: e.target.checked };
          }
          return el;
        }),
      );
    },
    [checkedElements],
  );

  useEffect(() => {
    if (!mappingEnabled) {
      return;
    }
    filteredSuggestions.forEach((dps) => {
      dps.forEach((dp) => {
        if (dp.type === 'CASINO' && casinoTemplatesEnabled) {
          setCasinoAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
        if (elementSelectionHelper.isTeamAssetDataPoint(dp.dataPointName)) {
          setTeamAssetElIds((prev) => [...prev.filter((id) => id !== dp.elId), dp.elId]);
        }
      });
    });
  }, [filteredSuggestions, mappingEnabled, casinoTemplatesEnabled]);

  const handleToggleImgElType = (elId: string, newType: ImgElType) => {
    const elIsTeamAssetEl = teamAssetElIds.includes(elId);
    const elIsCasinoEl = casinoAssetElIds.includes(elId);

    if (newType === 'teamasset') {
      if (!elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }

      return;
    }

    if (newType === 'img') {
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
      if (elIsCasinoEl) {
        setCasinoAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }

    if (newType === 'casino') {
      if (!elIsCasinoEl) {
        setCasinoAssetElIds((prev) => [...prev, elId]);
      }
      if (elIsTeamAssetEl) {
        setTeamAssetElIds((prev) => prev.filter((id) => id !== elId));
      }
    }
  };

  const onToggleMapping = useCallback(
    (selected: boolean) => {
      setCheckedElements(
        checkedElements.map((el) => {
          const match = filteredSuggestions.find((dps) => dps.some((dp) => dp.elId === el.id));
          if (match) {
            return { ...el, selected };
          }
          return el;
        }),
      );
      toggleDataMapping();
    },
    [checkedElements, toggleDataMapping, filteredSuggestions],
  );

  const getImageElementType = useCallback(
    (elId: string) => {
      if (!mappingEnabled) {
        return;
      }
      const match = filteredSuggestions.find((dps) => dps.some((dp) => dp.elId === elId));
      if (!match) {
        return;
      }
      if (teamAssetElIds.includes(elId)) return 'teamasset';
      if (casinoAssetElIds.includes(elId)) return 'casino';
      return 'img';
    },
    [filteredSuggestions, mappingEnabled, teamAssetElIds, casinoAssetElIds],
  );

  return (
    <>
      <ModalBody display="flex" alignItems="flex-start" columnGap={5}>
        <Box>
          {state.previewURL && (
            <Image
              data-test-id="loaded-image"
              w={300}
              src={state.previewURL}
              fallback={<Skeleton height={300} width={300} />}
            ></Image>
          )}
          {!state.savedComponentId && (
            <Checkbox mt={3} key="toggleSave" onChange={toggleSaveComponent}>
              <Box display="flex" alignItems="center">
                <FaBookmark /> <Text ml={1}>Save this component for later</Text>
              </Box>
            </Checkbox>
          )}
        </Box>
        <Box flexDirection={'column'}>
          <Box
            borderWidth={1}
            borderRadius="md"
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <Heading as="h3" size="md">
              Element Selection
            </Heading>
            <Text size="md" mb={3}>
              Select any additional elements you would like to create.
            </Text>
            <Stack
              direction="column"
              maxH={'300px'}
              overflowY={'scroll'}
              data-test-id="elements-list"
            >
              {checkedElements.map((el, index) => {
                if (el.type === 'SVG') {
                  return (
                    <Checkbox pl={1} key={el.id} defaultChecked isDisabled>
                      <Box display={'flex'} alignItems="center">
                        <FaShapes /> <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'TEXT') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <Icon as={LuType}></Icon>
                        <Text ml={2} noOfLines={1} as="span">
                          {el.name}
                          <Text ml={2} fontStyle="italic" fontSize="xs" as="span">
                            - &quot;{el.data.content}&quot;
                          </Text>
                        </Text>
                      </Box>
                    </Checkbox>
                  );
                }
                if (el.type === 'IMAGE') {
                  return (
                    <Flex key={el.id} justifyContent="space-between">
                      <Checkbox pl={1} isChecked={el.selected} onChange={onChangeHandle(index)}>
                        <Box display={'flex'} alignItems="center">
                          {teamAssetElIds.includes(el.id) ? (
                            <GiAmericanFootballHelmet />
                          ) : casinoAssetElIds.includes(el.id) ? (
                            <CasinoIcon />
                          ) : (
                            <LuImage />
                          )}
                          <Text ml={2}>{el.name}</Text>
                        </Box>
                      </Checkbox>
                      <ImgElTypeRadioGroup
                        onChange={(type) => handleToggleImgElType(el.id, type)}
                        name={`${el.id}_assettype-toggle`}
                        val={getImageElementType(el.id)}
                        casinoTemplatesEnabled={casinoTemplatesEnabled}
                      />
                    </Flex>
                  );
                }
                if (el.type === 'VECTOR') {
                  return (
                    <Checkbox
                      pl={1}
                      key={el.id}
                      isChecked={el.selected}
                      onChange={onChangeHandle(index)}
                    >
                      <Box display={'flex'} alignItems="center">
                        <PiBoundingBox />
                        <Text ml={2}>{el.name}</Text>
                      </Box>
                    </Checkbox>
                  );
                }
                return null;
              })}
            </Stack>
          </Box>
          <Box
            borderWidth={1}
            borderRadius="md"
            mt={2}
            px={4}
            py={3}
            flex={1}
            data-test-id="select-element"
          >
            <DataMappingMagic
              mappingEnabled={mappingEnabled}
              onToggleMapping={onToggleMapping}
              suggestedDataSources={filteredSuggestions}
              soccerMomentsEnabled={soccerMomentsEnabled}
              inferredSoccerConfig={inferredSoccerConfig}
              selectedStage={confirmedStage}
              onStageOverride={setStageOverride}
            />
          </Box>
        </Box>
      </ModalBody>
      <ModalFooter>
        <Button data-test-id="close-button" variant="ghost" mr={3} onClick={onClose}>
          Close
        </Button>
        <Button colorScheme="blue" onClick={handleSubmit} data-test-id="finish-selection">
          Finish
        </Button>
      </ModalFooter>
    </>
  );
};

ComponentImportStepThree.displayName = 'ComponentImportStepThree';
export default ComponentImportStepThree;
